import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";

const HEADERS = ["name", "email", "phone", "role", "state", "city", "zip_code", "timeline", "budget_min", "budget_max", "financing", "notes"];

const TEMPLATE =
  HEADERS.join(",") +
  "\n" +
  'Jane Doe,jane@email.com,7025551234,buyer,NV,Las Vegas,89101,0-3 months,500000,800000,pre-approved,Wants a single-family home\n' +
  'John Smith,john@email.com,6025559876,seller,AZ,Phoenix,85018,3-6 months,450000,600000,cash,Relocating for work\n' +
  'Ava Lee,ava@email.com,6025551122,both,AZ,Phoenix,85018,0-3 months,650000,850000,pre-approved,Selling and upsizing';

function parseCSV(text) {
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n").filter((l) => l.trim().length);
  if (!lines.length) return { headers: [], rows: [] };
  const parseLine = (line) => {
    const out = [];
    let cur = "";
    let q = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (q) {
        if (c === '"') {
          if (line[i + 1] === '"') { cur += '"'; i++; } else q = false;
        } else cur += c;
      } else if (c === '"') q = true;
      else if (c === ",") { out.push(cur); cur = ""; }
      else cur += c;
    }
    out.push(cur);
    return out;
  };
  const headers = parseLine(lines[0]).map((h) => h.trim().toLowerCase());
  const rows = lines.slice(1).map((line) => {
    const vals = parseLine(line);
    const obj = {};
    headers.forEach((h, i) => (obj[h] = (vals[i] || "").trim()));
    return obj;
  });
  return { headers, rows };
}

export const BulkImportDialog = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const reset = () => { setRows([]); setFileName(""); setResult(null); };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      const { headers, rows } = parseCSV(String(reader.result));
      if (!headers.includes("name") || !headers.includes("email")) {
        toast.error("CSV must include at least 'name' and 'email' columns. Download the template.");
        return;
      }
      setFileName(file.name);
      setRows(rows);
      toast.success(`Parsed ${rows.length} row${rows.length === 1 ? "" : "s"} from ${file.name}`);
    };
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "estatelead_leads_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const doImport = async () => {
    if (!rows.length) { toast.error("Choose a CSV file first."); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/admin/leads/bulk", { rows });
      setResult(data);
      if (data.inserted > 0) toast.success(`Imported ${data.inserted} lead${data.inserted === 1 ? "" : "s"}.`);
      if (data.skipped > 0) toast.warning(`${data.skipped} row${data.skipped === 1 ? "" : "s"} skipped (see details).`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-slate-700 bg-transparent text-white hover:bg-slate-800 gap-2" data-testid="admin-bulk-import-btn">
          <Upload className="h-4 w-4" /> Import CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-emerald-400" /> Bulk Import Leads</DialogTitle></DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-start gap-3 text-sm text-slate-400 bg-slate-950 border border-slate-800 rounded-lg p-3">
            <FileSpreadsheet className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
            <div>
              Upload a CSV with columns: <span className="font-mono text-slate-300 text-xs">{HEADERS.join(", ")}</span>.
              Each row is validated and auto-scored. Invalid rows are skipped.
            </div>
          </div>

          <Button onClick={downloadTemplate} variant="ghost" className="text-emerald-400 hover:text-emerald-300 hover:bg-slate-800 gap-2 px-0" data-testid="admin-download-template-btn">
            <Download className="h-4 w-4" /> Download CSV template
          </Button>

          <div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="admin-csv-file-input" />
            <button
              onClick={() => fileRef.current?.click()}
              data-testid="admin-choose-csv-btn"
              className="w-full border-2 border-dashed border-slate-700 hover:border-emerald-500/50 rounded-xl p-6 text-center transition-colors"
            >
              <Upload className="h-6 w-6 text-slate-500 mx-auto mb-2" />
              <div className="text-sm text-slate-300 font-medium">{fileName || "Click to choose a CSV file"}</div>
              {rows.length > 0 && <div className="text-xs text-emerald-400 mt-1 font-mono">{rows.length} rows ready to import</div>}
            </button>
          </div>

          {result && (
            <div className="space-y-2" data-testid="admin-import-result">
              <div className="flex items-center gap-2 text-emerald-400 text-sm">
                <CheckCircle2 className="h-4 w-4" /> {result.inserted} imported · {result.skipped} skipped of {result.total}
              </div>
              {result.errors?.length > 0 && (
                <div className="max-h-40 overflow-y-auto bg-slate-950 border border-amber-500/20 rounded-lg p-3 space-y-1">
                  {result.errors.map((er, i) => (
                    <div key={i} className="text-xs text-amber-400 flex items-start gap-1.5">
                      <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> Row {er.row}: {er.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={doImport} disabled={importing || !rows.length} className="bg-emerald-400 text-slate-950 hover:bg-emerald-300 font-semibold" data-testid="admin-import-submit-btn">
            {importing ? "Importing…" : `Import ${rows.length || ""} Leads`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
